import { SphereDashboardAPITypes } from "@stellar/api-logic";
import {
  MemberRoleTypes,
  PartialSdbMemberTypes,
  SdbMemberTypes,
} from "@src/custom-types/member-types";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";

/** Determines whether the member is in type of company */
export function isCompanyMember(
  member: SdbMemberTypes
): member is
  | SphereDashboardAPITypes.ICompanyMemberBase
  | FaroCoreApiTypes.CompanyMemberDetails {
  return (
    "role" in member && isCompanyRole(member.role) && !("dateJoined" in member)
  );
}

/**
 * Determines whether the member is for company base or not
 * Based on the definition of sdbTeamMemberBase, it will also be returned as true
 */
export function isCompanyMemberBase(
  member: SdbMemberTypes
): member is SphereDashboardAPITypes.ICompanyMemberBase {
  return isCompanyMember(member) && !("userRole" in member);
}

/** Determine whether the type of member is from company with details or not */
export function isCompanyMemberDetails(
  member: SdbMemberTypes
): member is FaroCoreApiTypes.CompanyMemberDetails {
  return (
    isCompanyMember(member) &&
    "userRole" in member &&
    isCompanyRole(member.userRole)
  );
}

/** Determines whether the member is for projects or not */
export function isProjectMemberBase(
  member: SdbMemberTypes
): member is SphereDashboardAPITypes.IProjectMemberBase {
  return isProjectRole(member.role);
}

/** Determines whether the member is in type of group */
export function isGroupMember(
  member: unknown
): member is SphereDashboardAPITypes.IGroupMember {
  return (
    typeof member === "object" &&
    member !== null &&
    "role" in member &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows role inputs
    isCompanyRole(member.role as any) &&
    !("userRole" in member) &&
    "dateJoined" in member
  );
}

/** Returns whether the member type is in type of IUserDetails */
export function isUserDetails(
  user:
    | SphereDashboardAPITypes.IUserDetails
    | PartialSdbMemberTypes
    | SphereDashboardAPITypes.IGroupMember
): user is SphereDashboardAPITypes.IUserDetails {
  return (
    "activeRoles" in user &&
    "accountCreationDate" in user &&
    "defaultProjectCount" in user &&
    "haveCamera" in user &&
    "howFound" in user &&
    "profileImageUrl" in user
  );
}

/** Determines whether the member type is from user as manager or not */
export function isUserAsManager(
  member: SdbMemberTypes | SphereDashboardAPITypes.IUserAsManager
): member is SphereDashboardAPITypes.IUserAsManager {
  return !("role" in member) && !isUserDetails(member);
}

/** Determines whether the role is related to company or not */
export function isCompanyRole(
  role: MemberRoleTypes
): role is FaroCoreApiTypes.CompanyRole {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows AllCompanyRoles inputs
  return FaroCoreApiConst.allCompanyRoles.includes(role as any);
}

/** Determines whether the role is related to project or not */
export function isProjectRole(
  role: MemberRoleTypes
): role is FaroCoreApiTypes.ProjectRole {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows AllProjectRoles inputs
  return FaroCoreApiConst.allProjectRoles.includes(role as any);
}

// TODO: ST-646 Add new group viewer role: https://faro01.atlassian.net/browse/ST-3056
/** Determines whether the role is related to group or not */
export function isGroupRole(
  role: MemberRoleTypes
): role is SphereDashboardAPITypes.IAssignmentGroupRole {
  return [
    "COMPANY_MANAGER",
    "GROUP_VIEWER",
    "PROJECT_MANAGER",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows role inputs
  ].includes(role as any);
}
